import { Fragment, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Panel } from '@/components/Panel';
import { Metric, MetricGrid } from '@/components/Metric';
import { ScoreGauge } from '@/components/ScoreGauge';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconLock, IconReport, IconPrint } from '@/components/icons';
import { readToken } from '@/lib/theme';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import { useSession } from '@/store/session';
import { AUDIT_MODULES, MODULE_LABELS, useCompliance, type AuditModuleId } from '@/store/compliance';
import { FRAMEWORK_LABELS, SEVERITY_ORDER, type AuditSeverity } from '@/compliance/types';
import type { EvidenceStatus } from '@/compliance/evidence/types';
import { downloadEvidenceJson } from '@/compliance/evidence/exporters/json';
import { downloadEvidenceCsv } from '@/compliance/evidence/exporters/csv';
import { downloadEvidenceXlsx } from '@/compliance/evidence/exporters/xlsx';
import { printEvidencePack } from '@/compliance/evidence/exporters/pdf';
import { EvidencePrintReport } from './EvidencePrintReport';

/** Audit severity → design severity chip class. */
function sevChip(sev: AuditSeverity): string {
  if (sev === 'critical' || sev === 'high') return 'chip--critical';
  if (sev === 'medium') return 'chip--warn';
  return 'chip--info';
}
function statusChip(status: EvidenceStatus): string {
  if (status === 'pass') return 'chip--pass';
  if (status === 'fail') return 'chip--critical';
  if (status === 'partial') return 'chip--warn';
  return 'chip--info';
}

export function ComplianceDashboard({
  tenantName,
  tenantId,
  adminName,
}: {
  tenantName?: string | null;
  tenantId?: string | null;
  adminName?: string | null;
}) {
  const consented = useSession((s) => s.consented);
  const grant = useSession((s) => s.grantConsent);
  const has = useSession((s) => s.hasConsent);
  const { status, pack, error, runningModule, run } = useCompliance();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set<string>());

  function toggleRow(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selected = useMemo<AuditModuleId[]>(
    () => AUDIT_MODULES.filter((m) => consented.has(m.cap)).map((m) => m.id),
    [consented],
  );

  const red = readToken('--red', '#9E4F4F');
  const green = readToken('--green', '#3F7057');
  const orange = readToken('--orange', '#A97F3C');
  const accent = readToken('--accent', '#5C7E8F');
  const grid = readToken('--border', '#D4DDE2');
  const text3 = readToken('--text-3', '#6E7478');

  function runAudit() {
    void run(selected, {
      tenantName: tenantName ?? 'Unknown tenant',
      tenantId: tenantId ?? '',
      collectedBy: adminName ?? 'unknown',
    });
  }

  const coverageData =
    pack?.coverage
      .filter((c) => c.framework !== 'secScore' && c.total > 0)
      .map((c) => ({ label: FRAMEWORK_LABELS[c.framework], score: c.score })) ?? [];

  const passFailData = pack
    ? [
        { name: 'Passing', value: pack.meta.controlsPassed, fill: green },
        { name: 'Failing', value: pack.meta.controlsFailed, fill: red },
      ]
    : [];

  const severityData = pack
    ? SEVERITY_ORDER.map((s) => ({
        label: s.charAt(0).toUpperCase() + s.slice(1),
        count: pack.posture.breakdown[s],
        fill: s === 'critical' || s === 'high' ? red : s === 'medium' ? orange : accent,
      }))
    : [];

  const chartTooltip = {
    cursor: { fill: readToken('--bg-2', '#EFF3F6') },
    contentStyle: {
      borderRadius: 10,
      border: `1px solid ${grid}`,
      fontFamily: 'var(--font-mono)',
      fontSize: 12,
    },
  };

  return (
    <>
      <Panel
        id="audit"
        icon={<IconLock />}
        title="Compliance audit evidence"
        cap="AUDIT"
        why="Run the identity, device, and application audits, map every finding to CIS, NIST, ISO 27001, SOC 2, HIPAA, and Secure Score controls, and export auditor-ready evidence — entirely in this browser."
        action={
          pack ? (
            <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn--secondary btn--sm" onClick={() => downloadEvidenceJson(pack)}>JSON</button>
              <button className="btn btn--secondary btn--sm" onClick={() => downloadEvidenceCsv(pack)}>CSV</button>
              <button className="btn btn--secondary btn--sm" onClick={() => downloadEvidenceXlsx(pack)}>xlsx</button>
              <button className="btn btn--primary btn--sm" onClick={() => printEvidencePack()}>
                <IconPrint width={16} height={16} /> Evidence pack (PDF)
              </button>
            </div>
          ) : undefined
        }
      >
        {/* Module selection — checking a module is the point of consent for its scopes. */}
        <div className="no-print">
          <h3 className="metric__label">Audit scope — select modules to run</h3>
          <div className="modsel">
            {AUDIT_MODULES.map(({ id, cap }) => {
              const isOn = has(cap);
              return (
                <label key={id} className={`modsel__row${isOn ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isOn}
                    disabled={isOn}
                    onChange={() => grant(cap)}
                  />
                  <span className="modsel__name">{MODULE_LABELS[id]}</span>
                  <code className="modsel__scopes">{CAPABILITY_SCOPES[cap].scopes.join('  ·  ')}</code>
                </label>
              );
            })}
          </div>
          <div className="gate__cta" style={{ marginTop: 12 }}>
            <button
              className="btn btn--primary"
              onClick={runAudit}
              disabled={selected.length === 0 || status === 'running'}
            >
              {status === 'running'
                ? `Auditing ${runningModule ? MODULE_LABELS[runningModule] : ''}…`
                : pack
                  ? 'Re-run audit'
                  : 'Run compliance audit'}
            </button>
            {selected.length === 0 && (
              <p className="small muted" style={{ marginTop: 8 }}>
                Grant at least one module above to run the audit.
              </p>
            )}
          </div>
        </div>

        {status === 'error' && <ErrorNotice error={error} />}

        {pack && (
          <>
            <div className="gauge" style={{ marginTop: 18 }}>
              <ScoreGauge score={pack.posture.score} />
              <div className="gauge__body">
                <p className="gauge__grade">
                  Posture {pack.posture.score}/100 · Grade {pack.posture.grade}
                </p>
                <p className="gauge__caption">
                  {pack.meta.controlsTested} controls tested · {pack.meta.controlsPassed} passing ·{' '}
                  {pack.meta.controlsFailed} failing. Collected {new Date(pack.meta.collectedAt).toLocaleString()}.
                </p>
              </div>
            </div>

            <MetricGrid>
              <Metric label="Controls tested" value={pack.meta.controlsTested} />
              <Metric label="Passing" value={pack.meta.controlsPassed} tone="green" />
              <Metric label="Failing" value={pack.meta.controlsFailed}
                tone={pack.meta.controlsFailed > 0 ? 'red' : 'green'} />
              <Metric label="Critical + high" value={pack.posture.breakdown.critical + pack.posture.breakdown.high}
                tone={pack.posture.breakdown.critical + pack.posture.breakdown.high > 0 ? 'red' : 'green'} />
            </MetricGrid>

            {/* Charts */}
            <div className="chart-grid">
              <div>
                <h3 className="metric__label">Control coverage by framework</h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={coverageData} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: text3 }}
                        tickLine={false} axisLine={{ stroke: grid }} />
                      <YAxis type="category" dataKey="label" width={110}
                        tick={{ fontSize: 11, fill: text3 }} tickLine={false} axisLine={false} />
                      <Tooltip {...chartTooltip} />
                      <Bar dataKey="score" name="Coverage %" radius={[0, 4, 4, 0]}>
                        {coverageData.map((d, i) => (
                          <Cell key={i} fill={d.score >= 80 ? green : d.score >= 50 ? orange : red} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h3 className="metric__label">Pass / fail</h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={passFailData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={72}
                        paddingAngle={2}>
                        {passFailData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h3 className="metric__label">Findings by severity</h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={severityData} margin={{ top: 4, right: 8, bottom: 4, left: -18 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: text3 }} tickLine={false}
                        axisLine={{ stroke: grid }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: text3 }} tickLine={false}
                        axisLine={false} width={30} />
                      <Tooltip {...chartTooltip} />
                      <Bar dataKey="count" name="Findings" radius={[4, 4, 0, 0]}>
                        {severityData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Per-framework coverage table */}
            <h3 className="metric__label" style={{ marginTop: 8 }}>Framework coverage</h3>
            <table className="matrix">
              <thead>
                <tr><th>Framework</th><th>Tested</th><th>Passed</th><th>Failed</th><th>Coverage</th></tr>
              </thead>
              <tbody>
                {pack.coverage.filter((c) => c.total > 0).map((c) => (
                  <tr key={c.framework}>
                    <td className="matrix__label">{FRAMEWORK_LABELS[c.framework]}</td>
                    <td>{c.total}</td>
                    <td>{c.passed}</td>
                    <td>{c.failed}</td>
                    <td>
                      <span className={`chip ${c.score >= 80 ? 'chip--pass' : c.score >= 50 ? 'chip--warn' : 'chip--critical'}`}>
                        {c.score}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Evidence records — click a row to expand affected assets/configurations */}
            <h3 className="metric__label" style={{ marginTop: 14 }}>
              Evidence — {pack.records.length} controls <span className="small muted">(click a row to expand)</span>
            </h3>
            <table className="matrix matrix--expandable">
              <thead>
                <tr><th aria-label="expand" style={{ width: 24 }}></th><th>Status</th><th>Severity</th><th>Control</th><th>Evidence</th><th>Frameworks</th></tr>
              </thead>
              <tbody>
                {pack.records.map((r) => {
                  const isOpen = expanded.has(r.controlId);
                  const allCitations = [
                    ...r.frameworks.cis, ...r.frameworks.nist, ...r.frameworks.iso,
                    ...r.frameworks.soc2, ...r.frameworks.hipaa,
                  ];
                  return (
                    <Fragment key={r.controlId}>
                      <tr
                        className="matrix__row--toggle"
                        onClick={() => toggleRow(r.controlId)}
                        aria-expanded={isOpen}
                      >
                        <td className="matrix__caret">{isOpen ? '▾' : '▸'}</td>
                        <td><span className={`chip ${statusChip(r.status)}`}>{r.status}</span></td>
                        <td><span className={`chip ${sevChip(r.severity)}`}>{r.severity}</span></td>
                        <td className="matrix__label">
                          {r.title}
                          <span className="small muted" style={{ display: 'block' }}>{r.objective}</span>
                        </td>
                        <td className="matrix__detail">
                          {r.affected.length === 0 ? (
                            <span className="muted">—</span>
                          ) : (
                            <span>{r.affected.length} object{r.affected.length === 1 ? '' : 's'}</span>
                          )}
                        </td>
                        <td className="matrix__detail">
                          <code className="small">
                            {allCitations.slice(0, 4).join('  ')}
                            {allCitations.length > 4 ? ' …' : ''}
                          </code>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="matrix__detailrow">
                          <td></td>
                          <td colSpan={5}>
                            <div className="evrow">
                              <div className="evrow__block">
                                <span className="evrow__h">Affected assets / configurations ({r.affected.length})</span>
                                {r.affected.length === 0 ? (
                                  <p className="small muted">None — control passed.</p>
                                ) : (
                                  <ul className="evrow__list">
                                    {r.affected.map((a) => (
                                      <li key={a.id}>
                                        <span className="evrow__type">{a.type}</span>{' '}
                                        <strong>{a.label}</strong>
                                        {a.detail ? <span className="small muted"> — {a.detail}</span> : null}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div className="evrow__block">
                                <span className="evrow__h">Framework mappings</span>
                                <p className="small">
                                  {r.frameworks.cis.length > 0 && <><strong>CIS:</strong> {r.frameworks.cis.join(', ')}<br /></>}
                                  {r.frameworks.nist.length > 0 && <><strong>NIST:</strong> {r.frameworks.nist.join(', ')}<br /></>}
                                  {r.frameworks.iso.length > 0 && <><strong>ISO 27001:</strong> {r.frameworks.iso.join(', ')}<br /></>}
                                  {r.frameworks.soc2.length > 0 && <><strong>SOC 2:</strong> {r.frameworks.soc2.join(', ')}<br /></>}
                                  {r.frameworks.hipaa.length > 0 && <><strong>HIPAA:</strong> {r.frameworks.hipaa.join(', ')}<br /></>}
                                  {r.frameworks.secScore.length > 0 && <><strong>Secure Score:</strong> {r.frameworks.secScore.join('; ')}</>}
                                </p>
                              </div>
                              <div className="evrow__block">
                                <span className="evrow__h">Provenance</span>
                                <p className="small muted">
                                  <code>{r.provenance.endpoints.join('  ')}</code><br />
                                  Collected {new Date(r.provenance.collectedAt).toISOString()}
                                </p>
                                {r.status !== 'pass' && (
                                  <p className="small"><strong>Remediation:</strong> {r.remediation}</p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            <p className="small muted" style={{ marginTop: 14 }}>
              <IconReport width={13} height={13} /> Framework citations are drawn from published cross-mappings and are a
              starting point — your auditor validates final applicability. Compliance is a floor, not proof of security.
              Generated client-side; no tenant data left your browser.
            </p>
          </>
        )}
      </Panel>

      {/* Printable evidence pack (hidden on screen, shown by @media print). */}
      {pack && <EvidencePrintReport pack={pack} />}
    </>
  );
}
